import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TruckData, CalculationsSummaryInputs } from '../../components/InputForm/types/ESALTypes';
import { RootState } from '../../../../reduxService';

/**
 * Redux state for ESAL Calculation Modal
 */
export interface EsalState {
    trucks: TruckData[];
    summaryInputs: CalculationsSummaryInputs;
}

/**
 * Initial state for ESAL Calculation Modal
 */
export const initialState: EsalState = {
    trucks: [],
    summaryInputs: {
        dd: 0.5,
        dl: 0.9,
        analysisPeriod: 12,
        growthRate: 2,
        trafficDesignLane: 0,
    },
};

/**
 * Redux slice for ESAL Calculation Modal state
 */
export const esalSlice = createSlice({
    name: 'esal',
    initialState,
    reducers: {
        /**
         * Set all trucks data
         */
        setTrucks(state, action: PayloadAction<TruckData[]>) {
            state.trucks = action.payload;
        },
        /**
         * Update a single truck by id
         */
        updateTruck(state, action: PayloadAction<TruckData>) {
            const idx = state.trucks.findIndex(t => t.id === action.payload.id);
            if (idx !== -1) {
                state.trucks[idx] = action.payload;
            }
        },
        /**
         * Set all summary inputs with validation
         */
        setSummaryInputs(state, action: PayloadAction<CalculationsSummaryInputs>) {
            // Basic validation to ensure values are within reasonable ranges
            const { dd, dl, analysisPeriod, growthRate, trafficDesignLane } = action.payload;

            state.summaryInputs = {
                dd: Math.max(0, Math.min(1, dd)), // 0-1 range
                dl: Math.max(0, Math.min(1, dl)), // 0-1 range
                analysisPeriod: Math.max(1, Math.min(100, analysisPeriod)), // 1-100 years
                growthRate: Math.max(-10, Math.min(50, growthRate)), // -10% to 50%
                trafficDesignLane: Math.max(0, trafficDesignLane), // Non-negative
            };
        },
        /**
         * Reset ESAL state to initial values
         */
        resetEsalState(state) {
            state.trucks = [];
            state.summaryInputs = initialState.summaryInputs;
        },
    },
});

/**
 * Selector for ESAL trucks
 * Returns initialState.trucks if state.Dynamic.esal is undefined
 */
export const selectEsalTrucks = (state: RootState) => state.Dynamic?.esal?.trucks ?? initialState.trucks;

/**
 * Selector for ESAL summary inputs
 * Returns initialState.summaryInputs if state.Dynamic.esal is undefined
 */
export const selectEsalSummaryInputs = (state: RootState) => state.Dynamic?.esal?.summaryInputs ?? initialState.summaryInputs;

export const { setTrucks, updateTruck, setSummaryInputs, resetEsalState } = esalSlice.actions;
export default esalSlice.reducer;
